import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { adminOrderApi, dashboardApi } from '../../services';
import toast from 'react-hot-toast';
import { 
  Users, 
  TrendingUp, 
  DollarSign, 
  ShoppingCart,
  BarChart3,
  Calendar,
  Filter,
  Eye,
  Download,
  RefreshCw,
  TrendingDown,
  FileText,
  Printer,
  X,
  Package,
  User,
  Star,
  Clock,
  Tag,
  MapPin,
  Phone,
  Mail
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Custom CSS for larger date picker
const customDatePickerStyles = `
  .react-datepicker {
    font-size: 16px !important;
    width: 320px !important;
  }
  .react-datepicker__header {
    padding: 20px !important;
  }
  .react-datepicker__current-month {
    font-size: 18px !important;
    font-weight: bold !important;
  }
  .react-datepicker__day-name {
    font-size: 14px !important;
    padding: 8px !important;
  }
  .react-datepicker__day {
    font-size: 16px !important;
    padding: 10px !important;
    margin: 2px !important;
  }
  .react-datepicker__day:hover {
    background-color: #3b82f6 !important;
    color: white !important;
  }
  .react-datepicker__day--selected {
    background-color: #3b82f6 !important;
    color: white !important;
  }
  .react-datepicker__day--in-range {
    background-color: #dbeafe !important;
    color: #1e40af !important;
  }
  .react-datepicker__day--keyboard-selected {
    background-color: #3b82f6 !important;
    color: white !important;
  }
  .react-datepicker__navigation {
    top: 20px !important;
  }
  .react-datepicker__navigation-icon {
    font-size: 18px !important;
  }
  
  /* Custom CSS for chart optimization on small laptop screens */
  @media (max-width: 1366px) {
    .chart-container {
      height: 400px !important;
    }
  }
  
  @media (max-width: 1024px) {
    .chart-container {
      height: 350px !important;
    }
  }
  
  @media (max-width: 768px) {
    .chart-container {
      height: 300px !important;
    }
  }
  
  /* Optimize chart responsiveness */
  .chart-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 300px;
  }
  
  /* Ensure chart maintains aspect ratio on small screens */
  @media (max-width: 640px) {
    .chart-container {
      height: 250px !important;
    }
  }
  
  /* Optimize for small laptop screens (1366px and below) */
  @media (max-width: 1366px) {
    .dashboard-container {
      max-width: 100% !important;
      padding: 0.5rem !important;
    }
    
    .chart-section {
      margin-bottom: 1rem !important;
    }
    
    .stats-cards {
      grid-template-columns: repeat(2, 1fr) !important;
      gap: 0.5rem !important;
    }
    
    .secondary-charts {
      grid-template-columns: 1fr !important;
      gap: 1rem !important;
    }
  }
  
  /* Fullscreen chart optimization */
  .chart-fullscreen {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    width: 100vw !important;
    height: 100vh !important;
    z-index: 9999 !important;
    background: white !important;
    padding: 1rem !important;
  }
`;

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const AdminDashboard = () => {
  const { token } = useAuth();
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [timeRange, setTimeRange] = useState('today');

  const [revenueTimeRange, setRevenueTimeRange] = useState('today');
  const [ordersTimeRange, setOrdersTimeRange] = useState('today');
  const [profitTimeRange, setProfitTimeRange] = useState('today');
  
  // Custom date range states
  const [startDate, setStartDate] = useState(new Date(new Date().getTime() - 30 * 24 * 60 * 60 * 1000));
  const [endDate, setEndDate] = useState(new Date());
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  
  // Export state
  const [isExporting, setIsExporting] = useState(false);
  
  // Popup states
  const [showProductPopup, setShowProductPopup] = useState(false);
  const [showSellerPopup, setShowSellerPopup] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [isChartFullscreen, setIsChartFullscreen] = useState(false);
  const [chartKey, setChartKey] = useState(0); // Force re-render chart
  
  // Stats state
  const [currentStats, setCurrentStats] = useState({
    totalRevenue: 0,
    totalOrders: 0,
    totalCost: 0,
    totalItems: 0,
    totalCustomers: 0,
    avgOrderValue: 0,
    completionRate: 0,
    processingOrders: 0,
    completedRevenue: 0,
    completedProfit: 0
  });
  
  // Load data khi component mount
  useEffect(() => {
    loadData();
  }, [token]);

  // Cập nhật stats và chart khi timeRange thay đổi
  useEffect(() => {
    console.log('Time range changed, updating stats for:', timeRange);
    const filteredOrders = getFilteredOrders(timeRange);
    console.log('Filtered orders count:', filteredOrders.length);
    console.log('All orders count:', allOrders.length);
    
    const stats = getStatsFromAPI(timeRange);
    setCurrentStats(stats);
    console.log('Updated stats:', stats);
    
    // Force re-render chart
    setChartKey(prev => prev + 1);
  }, [timeRange, allOrders]);

  // Load data từ API
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await adminOrderApi.getOrdersWithDateRange(token, '/orders');
      
      if (response.data && response.data.success) {
        setAllOrders(response.data.data || []);
      } else if (response.data && Array.isArray(response.data)) {
        setAllOrders(response.data);
      } else {
        console.error('Invalid API response format:', response);
        setError('Dữ liệu API không đúng định dạng');
        setAllOrders([]);
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
      setError(`Lỗi khi tải dữ liệu: ${error.message}`);
      setAllOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // Hàm lọc đơn hàng theo thời gian
  const getFilteredOrders = (timeRange = 'today') => {
    console.log('getFilteredOrders called with timeRange:', timeRange);
    console.log('allOrders count:', allOrders?.length || 0);
    
    if (!allOrders || allOrders.length === 0) return [];
    
    const now = new Date();
    let startOfPeriod, endOfPeriod;
    
    switch (timeRange) {
      case 'today':
        // Hôm nay
        startOfPeriod = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        endOfPeriod = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        break;
        
      case 'week':
        // Tuần này (từ thứ 2 đến chủ nhật)
        const dayOfWeek = now.getDay();
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        startOfPeriod = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysFromMonday);
        endOfPeriod = new Date(startOfPeriod.getFullYear(), startOfPeriod.getMonth(), startOfPeriod.getDate() + 6, 23, 59, 59, 999);
        break;
        
      case 'month':
        // Tháng này
        startOfPeriod = new Date(now.getFullYear(), now.getMonth(), 1);
        endOfPeriod = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
        break;
        
      case 'year':
        // Năm này
        startOfPeriod = new Date(now.getFullYear(), 0, 1);
        endOfPeriod = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
        break;
        
      case 'custom':
        if (startDate && endDate) {
          startOfPeriod = new Date(startDate);
          endOfPeriod = new Date(endDate);
          endOfPeriod.setHours(23, 59, 59, 999);
        } else {
          // Mặc định là hôm nay
          startOfPeriod = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          endOfPeriod = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        }
        break;
        
      default:
        // Mặc định là hôm nay
        startOfPeriod = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        endOfPeriod = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    }
    
    return allOrders.filter(order => {
      const orderDate = new Date(order.created_at);
      return orderDate >= startOfPeriod && orderDate <= endOfPeriod;
    });
  };
  
  // Date range validation
  const validateDateRange = (start, end) => {
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 31; // Maximum 31 days
  };

  // Handle time range change
  const handleTimeRangeChange = (newTimeRange) => {
    console.log('Changing time range to:', newTimeRange);
    setTimeRange(newTimeRange);
    setRevenueTimeRange(newTimeRange);
    setOrdersTimeRange(newTimeRange);
    setProfitTimeRange(newTimeRange);
    setShowCustomDatePicker(false);
    
    // Force re-render chart
    setChartKey(prev => prev + 1);
  };

  // Handle custom date range
  const handleCustomDateRange = () => {
    console.log('Applying custom date range:', { startDate, endDate });
    
    // Nếu chỉ chọn ngày bắt đầu, mặc định đến hôm nay
    if (startDate && !endDate) {
      setEndDate(new Date());
    }
    
    // Nếu chỉ chọn ngày kết thúc, mặc định từ 30 ngày trước
    if (!startDate && endDate) {
      const thirtyDaysAgo = new Date(endDate);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      setStartDate(thirtyDaysAgo);
    }
    
    // Nếu chưa chọn ngày nào, mặc định hôm nay
    if (!startDate && !endDate) {
      setStartDate(new Date());
      setEndDate(new Date());
    }
    
    if (validateDateRange(startDate, endDate)) {
      console.log('Setting time range to custom');
      setTimeRange('custom');
      setRevenueTimeRange('custom');
      setOrdersTimeRange('custom');
      setProfitTimeRange('custom');
      setShowCustomDatePicker(false);
      
      // Force re-render chart
      setChartKey(prev => prev + 1);
    } else {
      alert('Khoảng thời gian không được vượt quá 31 ngày!');
    }
  };

  // Handle start date change
  const handleStartDateChange = (date) => {
    setStartDate(date);
    // Nếu đã có endDate và startDate > endDate, reset endDate
    if (endDate && date > endDate) {
      setEndDate(null);
    }
  };

  // Handle end date change
  const handleEndDateChange = (date) => {
    setEndDate(date);
    // Nếu đã có startDate và endDate < startDate, reset startDate
    if (startDate && date < startDate) {
      setStartDate(null);
    }
  };

  // Export report function
  const exportReport = async () => {
    setIsExporting(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Create report data
      const reportData = {
        timeRange: timeRange,
        startDate: startDate,
        endDate: endDate,
        stats: getStatsFromAPI(timeRange),
        revenueData: getCurrentRevenueData(),
        ordersData: getCurrentOrdersData(),
        profitData: getCurrentProfitData(),
        topProducts: getTopProductsFromAPI(timeRange),
        topSellers: getTopSellersFromAPI(timeRange),
        exportDate: new Date().toISOString()
      };

      // Create CSV content
      const csvContent = generateCSVReport(reportData);
      
      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `bao-cao-dashboard-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      alert('Xuất báo cáo thành công!');
    } catch (error) {
      alert('Có lỗi xảy ra khi xuất báo cáo!');
    } finally {
      setIsExporting(false);
    }
  };

  // Handle product click
  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setShowProductPopup(true);
  };

  // Handle seller click
  const handleSellerClick = (seller) => {
    setSelectedSeller(seller);
    setShowSellerPopup(true);
  };

  // Close popups
  const closeProductPopup = () => {
    setShowProductPopup(false);
    setSelectedProduct(null);
  };

  const closeSellerPopup = () => {
    setShowSellerPopup(false);
    setSelectedSeller(null);
  };

  // Generate CSV report
  const generateCSVReport = (data) => {
    let csv = 'Báo cáo Dashboard\n\n';
    csv += `Thời gian: ${timeRange === 'custom' ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}` : timeRange}\n`;
    csv += `Ngày xuất: ${new Date().toLocaleDateString()}\n\n`;
    
    // Stats
    csv += 'Thống kê tổng quan\n';
    csv += 'Chỉ số,Giá trị\n';
    csv += `Tổng người dùng,${data.stats.totalUsers}\n`;
    csv += `Người dùng mới,${data.stats.newUsers}\n`;
    csv += `Doanh thu,${formatCurrency(data.stats.revenue)}\n`;
    csv += `Chi phí,${formatCurrency(data.stats.expenses)}\n`;
    csv += `Lợi nhuận,${formatCurrency(data.stats.profit)}\n`;
    csv += `Đơn hàng,${data.stats.orders}\n`;
    csv += `Tăng trưởng,${data.stats.growth}%\n\n`;
    
    // Revenue data
    csv += 'Dữ liệu doanh thu\n';
    csv += 'Thời gian,Doanh thu,Tăng trưởng\n';
    data.revenueData.forEach(item => {
      csv += `${getTimeLabel(item, 'revenue')},${formatCurrency(item.revenue)},${item.growth}%\n`;
    });
    csv += '\n';
    
    // Orders data
    csv += 'Dữ liệu đơn hàng\n';
    csv += 'Thời gian,Đơn hàng,Tăng trưởng\n';
    data.ordersData.forEach(item => {
      csv += `${getTimeLabel(item, 'orders')},${item.orders},${item.growth}%\n`;
    });
    csv += '\n';
    
    // Profit data
    csv += 'Dữ liệu lợi nhuận\n';
    csv += 'Thời gian,Lợi nhuận,Tăng trưởng\n';
    data.profitData.forEach(item => {
      csv += `${getTimeLabel(item, 'profit')},${formatCurrency(item.profit)},${item.growth}%\n`;
    });
    csv += '\n';
    
    // Top products
    csv += 'Top sản phẩm bán chạy\n';
    csv += 'Tên sản phẩm,Đã bán,Doanh thu,Tăng trưởng\n';
    data.topProducts.forEach(product => {
      csv += `${product.name},${product.sales},${formatCurrency(product.revenue)},${product.growth}%\n`;
    });
    csv += '\n';
    
    // Top sellers
    csv += 'Top seller doanh thu cao\n';
    csv += 'Tên seller,Đơn hàng,Doanh thu,Tăng trưởng\n';
    data.topSellers.forEach(seller => {
      csv += `${seller.name},${seller.orders},${formatCurrency(seller.revenue)},${seller.growth}%\n`;
    });
    
    return csv;
  };

  // Tính toán thống kê từ dữ liệu API đã lọc
  const getStatsFromAPI = (timeRange = 'today') => {
    const filteredOrders = getFilteredOrders(timeRange);
    if (!filteredOrders || filteredOrders.length === 0) {
      return {
        totalRevenue: 0,
        totalOrders: 0,
        totalCost: 0,
        totalItems: 0,
        totalCustomers: 0,
        avgOrderValue: 0,
        completionRate: 0,
        processingOrders: 0,
        completedRevenue: 0,
        completedProfit: 0
      };
    }

    // Debug: Log để kiểm tra dữ liệu
    console.log('Filtered orders:', filteredOrders.length);
    console.log('Order totals:', filteredOrders.map(order => ({ 
      id: order.id, 
      total: order.total, 
      parsed: parseFloat(order.total || 0) 
    })));
    
    const totalRevenue = filteredOrders.reduce((sum, order) => {
      const orderTotal = parseFloat(order.total || order.amount || 0);
      console.log(`Order ${order.id}: ${order.total || order.amount} -> ${orderTotal}`);
      return sum + orderTotal;
    }, 0);
    const totalOrders = filteredOrders.length;
    // Debug: Log order data to see available fields
    console.log('Sample order data:', filteredOrders.slice(0, 2).map(order => ({
      id: order.id,
      cost: order.cost,
      shipping_cost: order.shipping_cost,
      shipping_fee: order.shipping_fee,
      total: order.total,
      amount: order.amount
    })));
    
    const totalCost = filteredOrders.reduce((sum, order) => {
      const cost = parseFloat(order.cost || 0);
      const shippingCost = parseFloat(order.shipping_cost || order.shipping_fee || 0);
      const totalOrderCost = cost + shippingCost;
      console.log(`Order ${order.id}: cost=${cost}, shipping=${shippingCost}, total=${totalOrderCost}`);
      return sum + totalOrderCost;
    }, 0);
    const totalProfit = totalRevenue - totalCost;
    const uniqueCustomers = new Set(
      filteredOrders
        .map(order => order.customer_email || order.customer_mail || order.email)
        .filter(email => email && email.trim() !== '')
    ).size;
    const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
    const completedOrders = filteredOrders.filter(order => order.order_status === 'completed').length;
    const processingOrders = filteredOrders.filter(order => order.order_status === 'processing').length;
    const completionRate = totalOrders > 0 ? (completedOrders / totalOrders) * 100 : 0;
    
    // Tính tổng số items từ tất cả đơn hàng
    let totalItems = 0;
    filteredOrders.forEach(order => {
      try {
        const products = JSON.parse(order.products || '[]');
        products.forEach(product => {
          // Chỉ tính items có quantity hợp lệ
          const quantity = parseInt(product.quantity || 1);
          if (quantity > 0) {
            totalItems += quantity;
          }
        });
      } catch (error) {
        console.error('Error parsing products:', error);
      }
    });
    
    // Chỉ tính doanh thu và lợi nhuận cho đơn hàng đã hoàn thành
    const completedRevenue = filteredOrders
      .filter(order => order.order_status === 'completed')
      .reduce((sum, order) => sum + parseFloat(order.total || order.amount || 0), 0);
    
    const completedCost = filteredOrders
      .filter(order => order.order_status === 'completed')
      .reduce((sum, order) => {
        const cost = parseFloat(order.cost || 0);
        const shippingCost = parseFloat(order.shipping_cost || order.shipping_fee || 0);
        const totalOrderCost = cost + shippingCost;
        console.log(`Completed Order ${order.id}: cost=${cost}, shipping=${shippingCost}, total=${totalOrderCost}`);
        return sum + totalOrderCost;
      }, 0);
    
    const completedProfit = completedRevenue - completedCost;

    return {
      totalRevenue,
      totalOrders,
      totalCost,
      totalItems,
      totalCustomers: uniqueCustomers,
      avgOrderValue,
      completionRate,
      processingOrders,
      completedRevenue,
      completedProfit
    };
  };



  // Revenue data theo thời gian
  

  const getTopProductsFromAPI = (timeRange = 'today') => {
    const filteredOrders = getFilteredOrders(timeRange);
    if (!filteredOrders || filteredOrders.length === 0) {
      return [];
    }

    // Tạo map để theo dõi sản phẩm (chỉ dùng tên)
    const productMap = new Map();

    filteredOrders.forEach(order => {
      try {
        const products = JSON.parse(order.products || '[]');
        products.forEach(product => {
          // Xử lý product name null hoặc undefined
          let productName = product.name || product.product_name || 'Unknown Product';
          
          // Nếu tên có chứa "SKU:", loại bỏ phần SKU
          if (productName.includes('SKU:')) {
            productName = productName.split('SKU:')[0].trim();
          }
          
          // Nếu tên rỗng hoặc null, bỏ qua
          if (!productName || productName === 'Unknown Product') {
            return;
          }
          
          if (productMap.has(productName)) {
            const existing = productMap.get(productName);
            existing.sales += parseInt(product.quantity || 1);
            existing.revenue += parseFloat(product.total || product.price || 0);
            existing.count += 1;
          } else {
            productMap.set(productName, {
              name: productName,
              sales: parseInt(product.quantity || 1),
              revenue: parseFloat(product.total || product.price || 0),
              count: 1,
              growth: 0 // Không dùng random growth nữa
            });
          }
        });
      } catch (error) {
        console.error('Error parsing products:', error);
      }
    });

    // Chuyển đổi thành array và sắp xếp theo doanh thu
    const topProducts = Array.from(productMap.values())
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5)
      .map(product => ({
        name: product.name,
        sales: product.sales,
        revenue: product.revenue,
        growth: product.growth
      }));

    return topProducts;
  };

  const getTopSellersFromAPI = (timeRange = 'today') => {
    const filteredOrders = getFilteredOrders(timeRange);
    if (!filteredOrders || filteredOrders.length === 0) {
      return [];
    }

    // Tạo map để theo dõi seller
    const sellerMap = new Map();

    filteredOrders.forEach(order => {
      // Xử lý seller_mail null hoặc undefined
      const sellerEmail = order.seller_mail || order.seller_email || 'Unknown Seller';
      let sellerName = 'Unknown Seller';
      
      // Nếu có email, lấy tên từ email
      if (sellerEmail && sellerEmail !== 'Unknown Seller') {
        sellerName = sellerEmail.split('@')[0];
        // Capitalize first letter
        sellerName = sellerName.charAt(0).toUpperCase() + sellerName.slice(1);
      }
      
      if (sellerMap.has(sellerEmail)) {
        const existing = sellerMap.get(sellerEmail);
        existing.orders += 1;
        existing.revenue += parseFloat(order.total || 0);
      } else {
        sellerMap.set(sellerEmail, {
          name: sellerName,
          email: sellerEmail,
          orders: 1,
          revenue: parseFloat(order.total || 0),
          growth: 0 // Không dùng random growth nữa
        });
      }
    });

    // Chuyển đổi thành array và sắp xếp theo doanh thu
    const topSellers = Array.from(sellerMap.values())
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5)
      .map(seller => ({
        name: seller.name,
        orders: seller.orders,
        revenue: seller.revenue,
        growth: seller.growth
      }));

    return topSellers;
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(parseFloat(amount));
  };



  const getGrowthIcon = (growth) => {
    if (growth > 0) {
      return <TrendingUp className="w-4 h-4 text-green-600" />;
    } else if (growth < 0) {
      return <TrendingDown className="w-4 h-4 text-red-600" />;
    }
    return <BarChart3 className="w-4 h-4 text-gray-600" />;
  };

  const getGrowthColor = (growth) => {
    if (growth > 0) return 'text-green-600';
    if (growth < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const getCurrentRevenueData = () => {
    const filteredOrders = getFilteredOrders(timeRange);
    
    if (!filteredOrders || filteredOrders.length === 0) {
      return [];
    }

    // Sắp xếp đơn hàng theo ngày tạo
    const sortedOrders = [...filteredOrders].sort((a, b) => 
      new Date(a.created_at) - new Date(b.created_at)
    );

    // Tạo labels và data theo format thời gian
    let labels = [];
    let revenueData = [];

    switch (timeRange) {
      case 'today':
        // Nhóm theo giờ (0-23h)
        const hours = {};
        for (let i = 0; i < 24; i++) {
          hours[i] = { revenue: 0, orders: 0 };
        }
        
        sortedOrders.forEach(order => {
          const date = new Date(order.created_at);
          const hour = date.getHours();
          hours[hour].revenue += parseFloat(order.total || 0);
          hours[hour].orders += 1;
        });
        
        labels = Array.from({length: 24}, (_, i) => `${i}:00`);
        revenueData = Object.values(hours).map(h => h.revenue);
        break;
        
      case 'week':
        // Nhóm theo ngày trong tuần (0-6: CN, T2, T3, T4, T5, T6, T7)
        const days = {};
        for (let i = 0; i < 7; i++) {
          days[i] = { revenue: 0, orders: 0 };
        }
        
        sortedOrders.forEach(order => {
          const date = new Date(order.created_at);
          const dayOfWeek = date.getDay();
          days[dayOfWeek].revenue += parseFloat(order.total || 0);
          days[dayOfWeek].orders += 1;
        });
        
        const dayNames = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
        labels = dayNames;
        revenueData = Object.values(days).map(d => d.revenue);
        break;
        
      case 'month':
        // Nhóm theo ngày trong tháng (1-31)
        const monthDays = {};
        for (let i = 1; i <= 31; i++) {
          monthDays[i] = { revenue: 0, orders: 0 };
        }
        
        sortedOrders.forEach(order => {
          const date = new Date(order.created_at);
          const day = date.getDate();
          if (day <= 31) {
            monthDays[day].revenue += parseFloat(order.total || 0);
            monthDays[day].orders += 1;
          }
        });
        
        labels = Array.from({length: 31}, (_, i) => `${i + 1}`);
        revenueData = Object.values(monthDays).map(d => d.revenue);
        break;
        
      case 'year':
        // Nhóm theo tháng (0-11: T1-T12)
        const months = {};
        for (let i = 0; i < 12; i++) {
          months[i] = { revenue: 0, orders: 0 };
        }
        
        sortedOrders.forEach(order => {
          const date = new Date(order.created_at);
          const month = date.getMonth();
          months[month].revenue += parseFloat(order.total || 0);
          months[month].orders += 1;
        });
        
        const monthNames = ['T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9', 'T10', 'T11', 'T12'];
        labels = monthNames;
        revenueData = Object.values(months).map(m => m.revenue);
        break;
        
      case 'custom':
        // Nhóm theo ngày trong khoảng thời gian tùy chỉnh
        const customDays = {};
        if (startDate && endDate) {
          const start = new Date(startDate);
          const end = new Date(endDate);
          const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
          
          for (let i = 0; i <= daysDiff; i++) {
            const date = new Date(start);
            date.setDate(date.getDate() + i);
            const dateStr = date.toISOString().split('T')[0];
            customDays[dateStr] = { revenue: 0, orders: 0 };
          }
          
          sortedOrders.forEach(order => {
            const orderDate = new Date(order.created_at);
            const orderDateStr = orderDate.toISOString().split('T')[0];
            if (customDays[orderDateStr]) {
              customDays[orderDateStr].revenue += parseFloat(order.total || 0);
              customDays[orderDateStr].orders += 1;
            }
          });
          
          labels = Object.keys(customDays);
          revenueData = Object.values(customDays).map(d => d.revenue);
        }
        break;
        
      default:
        return [];
    }

    return revenueData.map((revenue, index) => ({
      label: labels[index] || `Day ${index + 1}`,
      revenue: revenue
    }));
  };

  const getCurrentOrdersData = () => {
    const filteredOrders = getFilteredOrders(timeRange);
    
    if (!filteredOrders || filteredOrders.length === 0) {
      return [];
    }

    // Sắp xếp đơn hàng theo ngày tạo
    const sortedOrders = [...filteredOrders].sort((a, b) => 
      new Date(a.date_created || a.created_at) - new Date(b.date_created || b.created_at)
    );

    // Tạo labels và data theo format thời gian
    let labels = [];
    let ordersData = [];

    switch (timeRange) {
      case 'today':
        // Nhóm theo giờ (0-23h)
        const hours = {};
        for (let i = 0; i < 24; i++) {
          hours[i] = { revenue: 0, orders: 0 };
        }
        
        sortedOrders.forEach(order => {
          const date = new Date(order.created_at);
          const hour = date.getHours();
          hours[hour].revenue += parseFloat(order.total || 0);
          hours[hour].orders += 1;
        });
        
        labels = Array.from({length: 24}, (_, i) => `${i}:00`);
        ordersData = Object.values(hours).map(h => h.orders);
        break;
        
      case 'week':
        // Nhóm theo ngày trong tuần (0-6: CN, T2, T3, T4, T5, T6, T7)
        const days = {};
        for (let i = 0; i < 7; i++) {
          days[i] = { revenue: 0, orders: 0 };
        }
        
        sortedOrders.forEach(order => {
          const date = new Date(order.created_at);
          const dayOfWeek = date.getDay();
          days[dayOfWeek].revenue += parseFloat(order.total || 0);
          days[dayOfWeek].orders += 1;
        });
        
        const dayNames = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
        labels = dayNames;
        ordersData = Object.values(days).map(d => d.orders);
        break;
        
      case 'month':
        // Nhóm theo ngày trong tháng (1-31)
        const monthDays = {};
        for (let i = 1; i <= 31; i++) {
          monthDays[i] = { revenue: 0, orders: 0 };
        }
        
        sortedOrders.forEach(order => {
          const date = new Date(order.created_at);
          const day = date.getDate();
          if (day <= 31) {
            monthDays[day].revenue += parseFloat(order.total || 0);
            monthDays[day].orders += 1;
          }
        });
        
        labels = Array.from({length: 31}, (_, i) => `${i + 1}`);
        ordersData = Object.values(monthDays).map(d => d.orders);
        break;
        
      case 'year':
        // Nhóm theo tháng (0-11: T1-T12)
        const months = {};
        for (let i = 0; i < 12; i++) {
          months[i] = { revenue: 0, orders: 0 };
        }
        
        sortedOrders.forEach(order => {
          const date = new Date(order.created_at);
          const month = date.getMonth();
          months[month].revenue += parseFloat(order.total || 0);
          months[month].orders += 1;
        });
        
        const monthNames = ['T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9', 'T10', 'T11', 'T12'];
        labels = monthNames;
        ordersData = Object.values(months).map(m => m.orders);
        break;
        
      case 'custom':
        // Nhóm theo ngày trong khoảng thời gian tùy chỉnh
        const customDays = {};
        if (startDate && endDate) {
          const start = new Date(startDate);
          const end = new Date(endDate);
          const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
          
          for (let i = 0; i <= daysDiff; i++) {
            const date = new Date(start);
            date.setDate(date.getDate() + i);
            const dateStr = date.toISOString().split('T')[0];
            customDays[dateStr] = { revenue: 0, orders: 0 };
          }
          
          sortedOrders.forEach(order => {
            const orderDate = new Date(order.created_at);
            const orderDateStr = orderDate.toISOString().split('T')[0];
            if (customDays[orderDateStr]) {
              customDays[orderDateStr].revenue += parseFloat(order.total || 0);
              customDays[orderDateStr].orders += 1;
            }
          });
          
          labels = Object.keys(customDays);
          ordersData = Object.values(customDays).map(d => d.orders);
        }
        break;
        
      default:
        return [];
    }

    return ordersData.map((orders, index) => ({
      label: labels[index] || `Day ${index + 1}`,
      orders: orders
    }));
  };

  const getCurrentProfitData = () => {
    const filteredOrders = getFilteredOrders(timeRange);
    
    if (!filteredOrders || filteredOrders.length === 0) {
      return [];
    }

    // Sắp xếp đơn hàng theo ngày tạo
    const sortedOrders = [...filteredOrders].sort((a, b) => 
      new Date(a.date_created || a.created_at) - new Date(b.date_created || b.created_at)
    );

    // Tạo labels và data theo format thời gian
    let labels = [];
    let profitData = [];

    switch (timeRange) {
      case 'today':
        // Nhóm theo giờ (0-23h)
        const hours = {};
        for (let i = 0; i < 24; i++) {
          hours[i] = { revenue: 0, cost: 0, profit: 0 };
        }
        
        sortedOrders.forEach(order => {
          const date = new Date(order.created_at);
          const hour = date.getHours();
          const revenue = parseFloat(order.total || 0);
          const cost = parseFloat(order.cost || 0) + parseFloat(order.shipping_cost || order.shipping_fee || 0);
          hours[hour].revenue += revenue;
          hours[hour].cost += cost;
          hours[hour].profit += (revenue - cost);
        });
        
        labels = Array.from({length: 24}, (_, i) => `${i}:00`);
        profitData = Object.values(hours).map(h => h.profit);
        break;
        
      case 'week':
        // Nhóm theo ngày trong tuần (0-6: CN, T2, T3, T4, T5, T6, T7)
        const days = {};
        for (let i = 0; i < 7; i++) {
          days[i] = { revenue: 0, cost: 0, profit: 0 };
        }
        
        sortedOrders.forEach(order => {
          const date = new Date(order.created_at);
          const dayOfWeek = date.getDay();
          const revenue = parseFloat(order.total || 0);
          const cost = parseFloat(order.cost || 0) + parseFloat(order.shipping_cost || order.shipping_fee || 0);
          days[dayOfWeek].revenue += revenue;
          days[dayOfWeek].cost += cost;
          days[dayOfWeek].profit += (revenue - cost);
        });
        
        const dayNames = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
        labels = dayNames;
        profitData = Object.values(days).map(d => d.profit);
        break;
        
      case 'month':
        // Nhóm theo ngày trong tháng (1-31)
        const monthDays = {};
        for (let i = 1; i <= 31; i++) {
          monthDays[i] = { revenue: 0, cost: 0, profit: 0 };
        }
        
        sortedOrders.forEach(order => {
          const date = new Date(order.created_at);
          const day = date.getDate();
          if (day <= 31) {
            const revenue = parseFloat(order.total || 0);
            const cost = parseFloat(order.cost || 0) + parseFloat(order.shipping_cost || order.shipping_fee || 0);
            monthDays[day].revenue += revenue;
            monthDays[day].cost += cost;
            monthDays[day].profit += (revenue - cost);
          }
        });
        
        labels = Array.from({length: 31}, (_, i) => `${i + 1}`);
        profitData = Object.values(monthDays).map(d => d.profit);
        break;
        
      case 'year':
        // Nhóm theo tháng (0-11: T1-T12)
        const months = {};
        for (let i = 0; i < 12; i++) {
          months[i] = { revenue: 0, cost: 0, profit: 0 };
        }
        
        sortedOrders.forEach(order => {
          const date = new Date(order.created_at);
          const month = date.getMonth();
          const revenue = parseFloat(order.total || 0);
          const cost = parseFloat(order.cost || 0) + parseFloat(order.shipping_cost || order.shipping_fee || 0);
          months[month].revenue += revenue;
          months[month].cost += cost;
          months[month].profit += (revenue - cost);
        });
        
        const monthNames = ['T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9', 'T10', 'T11', 'T12'];
        labels = monthNames;
        profitData = Object.values(months).map(m => m.profit);
        break;
        
      case 'custom':
        // Nhóm theo ngày trong khoảng thời gian tùy chỉnh
        const customDays = {};
        if (startDate && endDate) {
          const start = new Date(startDate);
          const end = new Date(endDate);
          const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
          
          for (let i = 0; i <= daysDiff; i++) {
            const date = new Date(start);
            date.setDate(date.getDate() + i);
            const dateStr = date.toISOString().split('T')[0];
            customDays[dateStr] = { revenue: 0, cost: 0, profit: 0 };
          }
          
          sortedOrders.forEach(order => {
            const orderDate = new Date(order.created_at);
            const orderDateStr = orderDate.toISOString().split('T')[0];
            if (customDays[orderDateStr]) {
              const revenue = parseFloat(order.total || 0);
              const cost = parseFloat(order.cost || 0) + parseFloat(order.shipping_cost || order.shipping_fee || 0);
              customDays[orderDateStr].revenue += revenue;
              customDays[orderDateStr].cost += cost;
              customDays[orderDateStr].profit += (revenue - cost);
            }
          });
          
          labels = Object.keys(customDays);
          profitData = Object.values(customDays).map(d => d.profit);
        }
        break;
        
      default:
        return [];
    }

    return profitData.map((profit, index) => ({
      label: labels[index] || `Day ${index + 1}`,
      profit: profit
    }));
  };

  const getTimeLabel = (item, type) => {
    // Sử dụng label từ item nếu có
    if (item.label) {
      return item.label;
    }
    
    // Fallback cho các trường hợp khác
    switch (timeRange) {
      case 'today':
        return item.date || item.hour || 'Giờ';
      case 'week':
        return item.week || item.day || 'Ngày';
      case 'month':
        return item.month || item.day || 'Ngày';
      case 'year':
        return item.year || item.month || 'Tháng';
      case 'custom':
        return item.date || 'Ngày';
      default:
        return item.label || item.date || 'Thời gian';
    }
  };

  const getMaxValue = (data, field) => {
    return Math.max(...data.map(item => item[field]));
  };

  // Base chart configuration
  const baseChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#6B7280',
          font: {
            size: 12,
          },
        },
      },
      y: {
        grid: {
          color: '#E5E7EB',
          borderDash: [5, 5],
        },
        ticks: {
          color: '#6B7280',
          font: {
            size: 12,
          },
        },
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
  };

  // Revenue chart options
  const revenueChartOptions = {
    ...baseChartOptions,
    plugins: {
      ...baseChartOptions.plugins,
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#fff',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += formatCurrency(context.parsed.y);
            }
            return label;
          }
        }
      },
    },
  };

  // Orders chart options
  const ordersChartOptions = {
    ...baseChartOptions,
    plugins: {
      ...baseChartOptions.plugins,
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#fff',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y.toLocaleString() + ' đơn hàng';
            }
            return label;
          }
        }
      },
    },
  };

  // Profit chart options
  const profitChartOptions = {
    ...baseChartOptions,
    plugins: {
      ...baseChartOptions.plugins,
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#fff',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += formatCurrency(context.parsed.y);
            }
            return label;
          }
        }
      },
    },
  };

  // Combined chart options with dual Y-axis
  const combinedChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 11,
          },
          boxWidth: 8,
          boxHeight: 8,
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#fff',
        borderWidth: 1,
        titleFont: {
          size: 12,
        },
        bodyFont: {
          size: 11,
        },
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              if (context.dataset.label === 'Revenue') {
                label += formatCurrency(context.parsed.y);
              } else if (context.dataset.label === 'Orders') {
                label += `${context.parsed.y} orders`;
              } else {
                label += formatCurrency(context.parsed.y);
              }
            }
            return label;
          },
          title: function(context) {
            const orderIndex = context[0].dataIndex;
            const order = window.recentOrdersForChart?.[orderIndex];
            if (order) {
              const date = new Date(order.date_created);
              return `${date.toLocaleDateString('vi-VN')} - Đơn hàng ${order.order_id}`;
            }
            return context[0].label;
          }
        }
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#6B7280',
          font: {
            size: 10,
          },
          maxRotation: 45,
          minRotation: 0,
        },
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        grid: {
          color: '#E5E7EB',
          borderDash: [5, 5],
        },
        ticks: {
          color: '#6B7280',
          font: {
            size: 10,
          },
          callback: function(value) {
            return formatCurrency(value);
          }
        },
        title: {
          display: true,
          text: 'Revenue (USD)',
          color: '#6B7280',
          font: {
            size: 10,
          },
        },
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          color: '#3B82F6',
          font: {
            size: 10,
          },
        },
        title: {
          display: true,
          text: 'Orders',
          color: '#3B82F6',
          font: {
            size: 10,
          },
        },
      },

    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
    elements: {
      point: {
        radius: 3,
        hoverRadius: 5,
      },
      line: {
        borderWidth: 2,
      },
    },
  };

  // Create chart data for revenue
  const getRevenueChartData = () => {
    const data = getCurrentRevenueData();
    return {
      labels: data.map(item => getTimeLabel(item, 'revenue')),
      datasets: [
        {
          label: 'Doanh thu',
          data: data.map(item => item.revenue),
          borderColor: '#10B981',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#10B981',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 6,
          pointHoverRadius: 8,
        },
      ],
    };
  };

  // Create chart data for orders
  const getOrdersChartData = () => {
    const data = getCurrentOrdersData();
    return {
      labels: data.map(item => getTimeLabel(item, 'orders')),
      datasets: [
        {
          label: 'Đơn hàng',
          data: data.map(item => item.orders),
          borderColor: '#8B5CF6',
          backgroundColor: 'rgba(139, 92, 246, 0.1)',
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#8B5CF6',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 6,
          pointHoverRadius: 8,
        },
      ],
    };
  };

  // Create chart data for profit
  const getProfitChartData = () => {
    const data = getCurrentProfitData();
    return {
      labels: data.map(item => getTimeLabel(item, 'profit')),
      datasets: [
        {
          label: 'Lợi nhuận',
          data: data.map(item => item.profit),
          borderColor: '#F59E0B',
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#F59E0B',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 6,
          pointHoverRadius: 8,
        },
      ],
    };
  };

  

  // Create combined chart data with real API data
  const getCombinedChartData = () => {
    const filteredOrders = getFilteredOrders(timeRange);
    
    if (!filteredOrders || filteredOrders.length === 0) {
      return {
        labels: ['Không có dữ liệu'],
        datasets: [
          {
            label: 'Doanh thu',
            data: [0],
            borderColor: '#10B981',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            borderWidth: 3,
            fill: true,
            tension: 0.4,
            pointBackgroundColor: '#10B981',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 6,
            pointHoverRadius: 8,
            yAxisID: 'y',
          },
          {
            label: 'Đơn hàng',
            data: [0],
            borderColor: '#3B82F6',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            borderWidth: 3,
            fill: false,
            tension: 0.4,
            pointBackgroundColor: '#3B82F6',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
            yAxisID: 'y1',
          },
        ],
      };
    }

    // Sắp xếp đơn hàng theo ngày tạo
    const sortedOrders = [...filteredOrders].sort((a, b) => 
      new Date(a.date_created || a.created_at) - new Date(b.date_created || b.created_at)
    );

    // Tạo labels và data theo format thời gian
    let labels = [];
    let revenueData = [];
    let ordersData = [];

    switch (timeRange) {
      case 'today':
        // Nhóm theo giờ (0-23h)
        const hours = {};
        for (let i = 0; i < 24; i++) {
          hours[i] = { revenue: 0, orders: 0 };
        }
        
        sortedOrders.forEach(order => {
          const date = new Date(order.created_at);
          const hour = date.getHours();
          hours[hour].revenue += parseFloat(order.total || 0);
          hours[hour].orders += 1;
        });
        
        labels = Array.from({length: 24}, (_, i) => `${i}:00`);
        revenueData = Object.values(hours).map(h => h.revenue);
        ordersData = Object.values(hours).map(h => h.orders);
        break;
        
      case 'week':
        // Nhóm theo ngày trong tuần (0-6: CN, T2, T3, T4, T5, T6, T7)
        const days = {};
        for (let i = 0; i < 7; i++) {
          days[i] = { revenue: 0, orders: 0 };
        }
        
        sortedOrders.forEach(order => {
          const date = new Date(order.created_at);
          const dayOfWeek = date.getDay();
          days[dayOfWeek].revenue += parseFloat(order.total || 0);
          days[dayOfWeek].orders += 1;
        });
        
        const dayNames = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
        labels = dayNames;
        revenueData = Object.values(days).map(d => d.revenue);
        ordersData = Object.values(days).map(d => d.orders);
        break;
        
      case 'month':
        // Nhóm theo ngày trong tháng (1-31)
        const monthDays = {};
        for (let i = 1; i <= 31; i++) {
          monthDays[i] = { revenue: 0, orders: 0 };
        }
        
        sortedOrders.forEach(order => {
          const date = new Date(order.created_at);
          const day = date.getDate();
          if (day <= 31) {
            monthDays[day].revenue += parseFloat(order.total || 0);
            monthDays[day].orders += 1;
          }
        });
        
        labels = Array.from({length: 31}, (_, i) => `${i + 1}`);
        revenueData = Object.values(monthDays).map(d => d.revenue);
        ordersData = Object.values(monthDays).map(d => d.orders);
        break;
        
      case 'year':
        // Nhóm theo tháng (0-11: T1-T12)
        const months = {};
        for (let i = 0; i < 12; i++) {
          months[i] = { revenue: 0, orders: 0 };
        }
        
        sortedOrders.forEach(order => {
          const date = new Date(order.created_at);
          const month = date.getMonth();
          months[month].revenue += parseFloat(order.total || 0);
          months[month].orders += 1;
        });
        
        const monthNames = ['T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9', 'T10', 'T11', 'T12'];
        labels = monthNames;
        revenueData = Object.values(months).map(m => m.revenue);
        ordersData = Object.values(months).map(m => m.orders);
        break;
        
      default:
        // Custom hoặc default: hiển thị theo ngày
        const groupedByDate = {};
        sortedOrders.forEach(order => {
          const date = new Date(order.created_at);
          const dateStr = date.toLocaleDateString('vi-VN');
          if (!groupedByDate[dateStr]) {
            groupedByDate[dateStr] = { revenue: 0, orders: 0 };
          }
          groupedByDate[dateStr].revenue += parseFloat(order.total || 0);
          groupedByDate[dateStr].orders += 1;
        });
        
        labels = Object.keys(groupedByDate);
        revenueData = Object.values(groupedByDate).map(d => d.revenue);
        ordersData = Object.values(groupedByDate).map(d => d.orders);
    }

    // Lưu dữ liệu vào window để tooltip có thể truy cập
    window.recentOrdersForChart = sortedOrders;

    return {
      labels: labels,
      datasets: [
        {
          label: 'Revenue',
          data: revenueData,
          borderColor: '#10B981',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#10B981',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 6,
          pointHoverRadius: 8,
          yAxisID: 'y',
        },
        {
          label: 'Orders',
          data: ordersData,
          borderColor: '#3B82F6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderWidth: 3,
          fill: false,
          tension: 0.4,
          pointBackgroundColor: '#3B82F6',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          yAxisID: 'y1',
        },
      ],
    };
  };

  return (
    <>
      <style>{customDatePickerStyles}</style>
    <div className="space-y-6 dashboard-container">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm lg:text-base text-gray-600 mt-1">Tổng quan hệ thống và thống kê chi tiết</p>
        </div>
        <div className="flex flex-col lg:flex-row items-center space-y-2 lg:space-y-0 lg:space-x-3 w-full lg:w-auto">
          {/* Time Range Selector */}
          <div className="flex flex-col lg:flex-row items-center space-y-2 lg:space-y-0 lg:space-x-2 w-full lg:w-auto">
          <select
            value={timeRange}
              onChange={(e) => handleTimeRangeChange(e.target.value)}
              className="w-full lg:w-auto px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="today">Hôm nay</option>
              <option value="week">Tuần này</option>
              <option value="month">Tháng này</option>
              <option value="year">Năm này</option>
          </select>
            
            {/* Custom Date Range Button */}
            <button
              onClick={() => setShowCustomDatePicker(!showCustomDatePicker)}
              className={`w-full lg:w-auto px-3 py-2 text-sm border rounded-lg transition-colors ${
                showCustomDatePicker 
                  ? 'border-blue-500 bg-blue-50 text-blue-600' 
                  : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
              }`}
              title="Chọn khoảng thời gian"
            >
              <Calendar className="w-4 h-4" />
            </button>
          </div>

                              {/* Custom Date Range Picker Modal */}
          {showCustomDatePicker && (
            <>
              {/* Backdrop */}
              <div 
                className="fixed inset-0 bg-black bg-opacity-50 z-50 backdrop-blur-sm"
                onClick={() => setShowCustomDatePicker(false)}
              ></div>
              
              {/* Modal */}
              <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 transform transition-all duration-300 ease-out">
                  {/* Header */}
                  <div className="flex items-center justify-between p-6 border-b border-gray-100">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Calendar className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">Chọn khoảng thời gian</h3>
                        <p className="text-sm text-gray-600">Tùy chỉnh khoảng thời gian xem dữ liệu</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowCustomDatePicker(false)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  
                  {/* Content */}
                  <div className="p-6 space-y-6">
                    {/* Date Pickers */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Từ ngày</label>
                        <DatePicker
                          selected={startDate}
                          onChange={handleStartDateChange}
                          selectsStart
                          startDate={startDate}
                          endDate={endDate}
                          maxDate={endDate || new Date()}
                          dateFormat="dd/MM/yyyy"
                          className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          placeholderText="Chọn ngày bắt đầu"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Đến ngày</label>
                        <DatePicker
                          selected={endDate}
                          onChange={handleEndDateChange}
                          selectsEnd
                          startDate={startDate}
                          endDate={endDate}
                          minDate={startDate}
                          maxDate={new Date()}
                          dateFormat="dd/MM/yyyy"
                          className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          placeholderText="Chọn ngày kết thúc"
                        />
                      </div>
                    </div>
                    
                    {/* Date Range Info */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-100">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <span className="text-sm font-medium text-gray-700">Khoảng thời gian</span>
                        </div>
                        <span className="text-sm font-bold text-blue-600">
                          {startDate && endDate 
                            ? `${Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24))} ngày`
                            : startDate 
                              ? 'Từ ngày bắt đầu đến hôm nay'
                              : endDate 
                                ? '30 ngày trước đến ngày kết thúc'
                                : 'Chưa chọn ngày'
                          }
                        </span>
                      </div>
                      <div className="mt-2 text-xs text-gray-500">
                        {startDate && endDate 
                          ? (validateDateRange(startDate, endDate) 
                              ? '✅ Khoảng thời gian hợp lệ' 
                              : '❌ Khoảng thời gian không được vượt quá 31 ngày')
                          : 'ℹ️ Chọn ít nhất một ngày để xem dữ liệu'
                        }
                      </div>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
                    <button
                      onClick={() => setShowCustomDatePicker(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Hủy
                    </button>
                    <button
                      onClick={handleCustomDateRange}
                      disabled={!startDate && !endDate}
                      className={`px-6 py-2 text-sm font-medium rounded-lg transition-colors ${
                        startDate || endDate
                          ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      Áp dụng
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col lg:flex-row items-center space-y-2 lg:space-y-0 lg:space-x-2 w-full lg:w-auto">
            <button 
              onClick={() => handleTimeRangeChange(timeRange)}
              className="w-full lg:w-auto p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Làm mới dữ liệu"
            >
            <RefreshCw className="w-5 h-5" />
          </button>
            <button
              onClick={exportReport}
              disabled={isExporting}
              className={`w-full lg:w-auto flex items-center justify-center space-x-2 px-4 py-2 text-sm rounded-lg transition-colors ${
                isExporting
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              {isExporting ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <FileText className="w-4 h-4" />
              )}
              <span>{isExporting ? 'Đang xuất...' : 'Xuất báo cáo'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="bg-white p-8 rounded-xl shadow-lg text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Đang tải dữ liệu...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 p-4 rounded-xl">
          <p className="text-red-600">{error}</p>
          <button 
            onClick={loadData}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Thử lại
          </button>
        </div>
      )}

      {/* Stats Cards - Responsive Grid */}
      {!loading && !error && (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 stats-cards">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 lg:p-6 rounded-xl shadow-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-xs lg:text-sm font-medium">Tổng khách hàng</p>
                              <p className="text-xl lg:text-3xl font-bold">{currentStats.totalCustomers.toLocaleString()}</p>
                <p className="text-blue-200 text-xs lg:text-sm">{currentStats.completionRate.toFixed(1)}% hoàn thành</p>
            </div>
            <div className="p-2 lg:p-3 bg-blue-400 rounded-lg">
              <Users className="w-6 h-6 lg:w-8 lg:h-8" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 p-4 lg:p-6 rounded-xl shadow-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-xs lg:text-sm font-medium">Doanh thu</p>
                              <p className="text-xl lg:text-3xl font-bold">{formatCurrency(currentStats.totalRevenue)}</p>
                <p className="text-green-200 text-xs lg:text-sm">{formatCurrency(currentStats.avgOrderValue)}/order</p>
            </div>
            <div className="p-2 lg:p-3 bg-green-400 rounded-lg">
              <DollarSign className="w-6 h-6 lg:w-8 lg:h-8" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-4 lg:p-6 rounded-xl shadow-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-xs lg:text-sm font-medium">Đơn hàng</p>
                              <p className="text-xl lg:text-3xl font-bold">{currentStats.totalOrders}</p>
                <p className="text-purple-200 text-xs lg:text-sm">{currentStats.totalItems} items</p>
            </div>
            <div className="p-2 lg:p-3 bg-purple-400 rounded-lg">
              <ShoppingCart className="w-6 h-6 lg:w-8 lg:h-8" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-4 lg:p-6 rounded-xl shadow-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-xs lg:text-sm font-medium">Chi phí</p>
                              <p className="text-xl lg:text-3xl font-bold">{formatCurrency(currentStats.totalCost)}</p>
              <p className="text-orange-200 text-xs lg:text-sm">Tổng chi phí</p>
            </div>
            <div className="p-2 lg:p-3 bg-orange-400 rounded-lg">
              <TrendingDown className="w-6 h-6 lg:w-8 lg:h-8" />
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Combined Development Chart */}
        <div className={`bg-white p-4 lg:p-6 rounded-xl shadow-lg border chart-section ${isChartFullscreen ? 'chart-fullscreen' : ''}`}>
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between mb-4 lg:mb-6">
            <div className="mb-4 lg:mb-0">
              <h3 className="text-lg lg:text-xl font-semibold text-gray-900">Biểu đồ doanh thu và đơn hàng</h3>
              <p className="text-sm text-gray-600 mt-1">
                Dữ liệu theo khoảng thời gian đã chọn
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-xs lg:text-sm text-gray-600">
                <span className="inline-block w-3 h-3 bg-green-500 rounded-full mr-1"></span>
                Revenue
              </span>
              <span className="text-xs lg:text-sm text-gray-600">
                <span className="inline-block w-3 h-3 bg-blue-500 rounded-full mr-1"></span>
                Orders
              </span>
              <button
                onClick={() => setIsChartFullscreen(!isChartFullscreen)}
                className="p-1.5 lg:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title={isChartFullscreen ? "Thu nhỏ biểu đồ" : "Phóng to biểu đồ"}
              >
                <Eye className="w-4 h-4 lg:w-5 lg:h-5" />
              </button>
            </div>
          </div>
          
          {/* Chart Container - 90% height for small laptop screens */}
          <div className={`chart-container chart-wrapper w-full transition-all duration-300 ${
            isChartFullscreen 
              ? 'h-[90vh]' 
              : 'h-64 sm:h-80 lg:h-[90vh] xl:h-[90vh] 2xl:h-[90vh]'
          }`}>
            <Line key={chartKey} data={getCombinedChartData()} options={combinedChartOptions} />
          </div>

          {/* Chart Summary - Responsive Grid */}
     


        </div>

  
      {/* Top Products and Sellers - Responsive Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        {/* Top Products */}
        <div className="bg-white p-4 lg:p-6 rounded-xl shadow-lg border">
          <div className="flex items-center justify-between mb-4 lg:mb-6">
            <h3 className="text-base lg:text-lg font-semibold text-gray-900">Top sản phẩm bán chạy</h3>
            <button className="p-1.5 lg:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
              <Download className="w-4 h-4 lg:w-5 lg:h-5" />
            </button>
          </div>
          
          <div className="space-y-3 lg:space-y-4">
            {getTopProductsFromAPI(timeRange).length > 0 ? (
              getTopProductsFromAPI(timeRange).map((product, index) => (
                <div 
                  key={index} 
                  className="p-3 lg:p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleProductClick(product)}
                >
                  <div className="flex items-center justify-between mb-2 lg:mb-3">
                    <div className="flex items-center space-x-2 lg:space-x-3">
                      <div className={`w-6 h-6 lg:w-8 lg:h-8 rounded-full flex items-center justify-center text-white font-bold text-xs lg:text-sm ${
                        index === 0 ? 'bg-yellow-500' : 
                        index === 1 ? 'bg-gray-400' : 
                        index === 2 ? 'bg-orange-500' : 'bg-blue-500'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <p className="text-sm lg:text-base font-medium text-gray-900">
                          {product.name || 'Unknown Product'}
                        </p>
                        <p className="text-xs lg:text-sm text-gray-600">
                          {product.sales || 0} sold
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm lg:text-base font-bold text-gray-900">
                        {formatCurrency(product.revenue || 0)}
                      </p>
                      <div className="flex items-center space-x-1">
                        {getGrowthIcon(product.growth || 0)}
                        <span className={`text-xs lg:text-sm ${getGrowthColor(product.growth || 0)}`}>
                          {product.growth > 0 ? '+' : ''}{product.growth || 0}%
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Mini Chart */}
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 h-1.5 lg:h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                        style={{ width: `${((product.sales || 0) / 150) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-xs text-gray-500">{product.sales || 0} sold</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-4 text-center text-gray-500">
                <Package className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <p>Không có dữ liệu sản phẩm</p>
              </div>
            )}
          </div>
        </div>

        {/* Top Sellers */}
        <div className="bg-white p-4 lg:p-6 rounded-xl shadow-lg border">
          <div className="flex items-center justify-between mb-4 lg:mb-6">
            <h3 className="text-base lg:text-lg font-semibold text-gray-900">Top seller doanh thu cao</h3>
            <button className="p-1.5 lg:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
              <Eye className="w-4 h-4 lg:w-5 lg:h-5" />
            </button>
          </div>
          
          <div className="space-y-3 lg:space-y-4">
            {getTopSellersFromAPI(timeRange).length > 0 ? (
              getTopSellersFromAPI(timeRange).map((seller, index) => (
                <div 
                  key={index} 
                  className="p-3 lg:p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSellerClick(seller)}
                >
                  <div className="flex items-center justify-between mb-2 lg:mb-3">
                    <div className="flex items-center space-x-2 lg:space-x-3">
                      <div className={`w-6 h-6 lg:w-8 lg:h-8 rounded-full flex items-center justify-center text-white font-bold text-xs lg:text-sm ${
                        index === 0 ? 'bg-yellow-500' : 
                        index === 1 ? 'bg-gray-400' : 
                        index === 2 ? 'bg-orange-500' : 'bg-blue-500'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <p className="text-sm lg:text-base font-medium text-gray-900">
                          {seller.name || 'Unknown Seller'}
                        </p>
                        <p className="text-xs lg:text-sm text-gray-600">
                          {seller.orders || 0} orders
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm lg:text-base font-bold text-gray-900">
                        {formatCurrency(seller.revenue || 0)}
                      </p>
                      <div className="flex items-center space-x-1">
                        {getGrowthIcon(seller.growth || 0)}
                        <span className={`text-xs lg:text-sm ${getGrowthColor(seller.growth || 0)}`}>
                          {seller.growth > 0 ? '+' : ''}{seller.growth || 0}%
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Mini Chart */}
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 h-1.5 lg:h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-green-500 to-blue-500 rounded-full"
                        style={{ width: `${((seller.revenue || 0) / 3500000) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-xs text-gray-500">{seller.orders || 0} orders</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-4 text-center text-gray-500">
                <User className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <p>Không có dữ liệu seller</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-4 lg:p-6 rounded-xl shadow-lg text-white">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between space-y-4 lg:space-y-0">
          <div>
            <h3 className="text-lg lg:text-xl font-bold mb-2">Cần hỗ trợ?</h3>
            <p className="text-sm lg:text-base text-blue-100">Xem báo cáo chi tiết hoặc liên hệ hỗ trợ</p>
          </div>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
            <button className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg transition-colors text-sm lg:text-base">
              Xem báo cáo
            </button>
            <button className="px-4 py-2 bg-white text-blue-600 hover:bg-gray-100 rounded-lg transition-colors text-sm lg:text-base">
              Liên hệ hỗ trợ
            </button>
          </div>
        </div>
      </div>
    </div>

    {/* Product Detail Popup */}
    {showProductPopup && selectedProduct && (
      <>
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50"
          onClick={closeProductPopup}
        ></div>
        
        {/* Modal */}
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <Package className="w-6 h-6 text-blue-600" />
                <h3 className="text-xl font-bold text-gray-900">Chi tiết sản phẩm</h3>
              </div>
              <button
                onClick={closeProductPopup}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Product Image and Basic Info */}
              <div className="flex flex-col lg:flex-row space-y-4 lg:space-y-0 lg:space-x-6">
                <div className="lg:w-1/3">
                  <div className="bg-gray-100 rounded-lg p-4 flex items-center justify-center">
                    <img
                      src={selectedProduct.image || 'https://via.placeholder.com/200x200?text=Product'}
                      alt={selectedProduct.name}
                      className="w-full h-48 object-cover rounded-lg"
                    />
                  </div>
                </div>
                
                <div className="lg:w-2/3 space-y-4">
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">{selectedProduct.name}</h4>
                    <p className="text-gray-600">{selectedProduct.description || 'Không có mô tả'}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <Tag className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-gray-700">Giá</span>
                      </div>
                      <p className="text-lg font-bold text-blue-600">{formatCurrency(selectedProduct.price)}</p>
                    </div>
                    
                    <div className="bg-green-50 p-3 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <ShoppingCart className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-gray-700">Đã bán</span>
                      </div>
                      <p className="text-lg font-bold text-green-600">{selectedProduct.sales}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Financial Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h5 className="font-semibold text-gray-900 mb-4">Tóm tắt tài chính ({timeRange})</h5>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white p-3 rounded-lg">
                    <div className="flex items-center space-x-2 mb-1">
                      <DollarSign className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-gray-700">Doanh thu</span>
                    </div>
                    <p className="text-lg font-bold text-green-600">{formatCurrency(selectedProduct.revenue)}</p>
                    <div className="flex items-center space-x-1 mt-1">
                      {getGrowthIcon(selectedProduct.growth)}
                      <span className={`text-sm ${getGrowthColor(selectedProduct.growth)}`}>
                        {selectedProduct.growth > 0 ? '+' : ''}{selectedProduct.growth}%
                      </span>
                    </div>
                  </div>
                  
                  <div className="bg-white p-3 rounded-lg">
                    <div className="flex items-center space-x-2 mb-1">
                      <TrendingUp className="w-4 h-4 text-red-600" />
                      <span className="text-sm font-medium text-gray-700">Chi phí</span>
                    </div>
                    <p className="text-lg font-bold text-red-600">{formatCurrency(selectedProduct.cost || selectedProduct.revenue * 0.6)}</p>
                  </div>
                  
                  <div className="bg-white p-3 rounded-lg">
                    <div className="flex items-center space-x-2 mb-1">
                      <BarChart3 className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-gray-700">Lợi nhuận</span>
                    </div>
                    <p className="text-lg font-bold text-blue-600">{formatCurrency(selectedProduct.profit || selectedProduct.revenue * 0.4)}</p>
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h6 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <Clock className="w-4 h-4 mr-2" />
                    Thông tin thời gian
                  </h6>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Ngày đăng:</span>
                      <span className="font-medium">{selectedProduct.createdAt || '01/01/2024'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Cập nhật cuối:</span>
                      <span className="font-medium">{selectedProduct.updatedAt || '15/01/2024'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Trạng thái:</span>
                      <span className="font-medium text-green-600">Đang bán</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h6 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <Star className="w-4 h-4 mr-2" />
                    Đánh giá & Phân loại
                  </h6>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Đánh giá:</span>
                      <span className="font-medium">4.5/5 ⭐</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Danh mục:</span>
                      <span className="font-medium">{selectedProduct.category || 'Điện tử'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Thương hiệu:</span>
                      <span className="font-medium">{selectedProduct.brand || 'Unknown'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    )}

    {/* Seller Detail Popup */}
    {showSellerPopup && selectedSeller && (
      <>
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50"
          onClick={closeSellerPopup}
        ></div>
        
        {/* Modal */}
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <User className="w-6 h-6 text-green-600" />
                <h3 className="text-xl font-bold text-gray-900">Chi tiết Seller</h3>
              </div>
              <button
                onClick={closeSellerPopup}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Seller Profile */}
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gradient-to-r from-green-400 to-blue-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">
                    {selectedSeller.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-900">{selectedSeller.name}</h4>
                  <p className="text-gray-600">{selectedSeller.email || 'seller@example.com'}</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-500">{selectedSeller.location || 'Hà Nội, Việt Nam'}</span>
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h5 className="font-semibold text-gray-900 mb-3">Thông tin liên hệ</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-center space-x-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span className="text-sm">{selectedSeller.phone || '+84 123 456 789'}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span className="text-sm">{selectedSeller.email || 'seller@example.com'}</span>
                  </div>
                </div>
              </div>

              {/* Performance Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h5 className="font-semibold text-gray-900 mb-4">Hiệu suất kinh doanh ({timeRange})</h5>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white p-3 rounded-lg">
                    <div className="flex items-center space-x-2 mb-1">
                      <ShoppingCart className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-gray-700">Đơn hàng</span>
                    </div>
                    <p className="text-lg font-bold text-blue-600">{selectedSeller.orders}</p>
                    <div className="flex items-center space-x-1 mt-1">
                      {getGrowthIcon(selectedSeller.growth)}
                      <span className={`text-sm ${getGrowthColor(selectedSeller.growth)}`}>
                        {selectedSeller.growth > 0 ? '+' : ''}{selectedSeller.growth}%
                      </span>
                    </div>
                  </div>
                  
                  <div className="bg-white p-3 rounded-lg">
                    <div className="flex items-center space-x-2 mb-1">
                      <DollarSign className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-gray-700">Doanh thu</span>
                    </div>
                    <p className="text-lg font-bold text-green-600">{formatCurrency(selectedSeller.revenue)}</p>
                  </div>
                  
                  <div className="bg-white p-3 rounded-lg">
                    <div className="flex items-center space-x-2 mb-1">
                      <BarChart3 className="w-4 h-4 text-purple-600" />
                      <span className="text-sm font-medium text-gray-700">Lợi nhuận</span>
                    </div>
                    <p className="text-lg font-bold text-purple-600">{formatCurrency(selectedSeller.profit || selectedSeller.revenue * 0.3)}</p>
                  </div>
                </div>
              </div>

              {/* Additional Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h6 className="font-semibold text-gray-900 mb-3">Thống kê chi tiết</h6>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Sản phẩm đang bán:</span>
                      <span className="font-medium">{selectedSeller.products || 25}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Đánh giá trung bình:</span>
                      <span className="font-medium">4.8/5 ⭐</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Thời gian hoạt động:</span>
                      <span className="font-medium">2 năm 3 tháng</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h6 className="font-semibold text-gray-900 mb-3">Trạng thái</h6>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Trạng thái:</span>
                      <span className="font-medium text-green-600">Đang hoạt động</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Xác thực:</span>
                      <span className="font-medium text-green-600">Đã xác thực</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Cấp độ:</span>
                      <span className="font-medium text-blue-600">Gold Seller</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    )}
    </>
  );
};

export default AdminDashboard; 
